/**
 * 리치 텍스트(전달사항 등)의 인라인 스타일·클래스를 허용 목록으로 좁힌다 (GS A2-21).
 *
 * DOMPurify 는 태그·속성 단위로만 거른다. `style` 을 허용하면 `position:fixed;z-index:99999`
 * 같은 값이 그대로 남아 화면(관리자 상세 포함) 위를 덮었다. 서버 저장 단계에서도 같은 목록으로
 * 거르지만(sanitize-html.decorator), 이미 저장된 옛 값이 있으니 그리는 쪽에서도 한 번 더 거른다.
 *
 * 남기는 것: 편집기(TipTap)가 내는 글자색·배경색·정렬·글자 크기, 링크의 모양 클래스.
 * 반드시 DOMPurify 로 정화한 HTML 을 넘긴다 — 이 함수는 정화기가 아니라 «좁히기» 다.
 */

const COLOR_VALUE = [
  /^#[0-9a-f]{3,8}$/i,
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d+)\s*)?\)$/i,
];

const ALLOWED_STYLE_VALUES: Record<string, RegExp[]> = {
  color: COLOR_VALUE,
  "background-color": [...COLOR_VALUE, /^transparent$/i],
  "text-align": [/^(left|right|center|justify)$/i],
  "font-size": [/^\d{1,2}(\.\d+)?px$/i, /^\d(\.\d+)?r?em$/i],
};

const ALLOWED_LINK_CLASSES = new Set([
  "text-blue-600",
  "underline",
  "cursor-pointer",
  "hover:text-blue-700",
]);

function restrictStyle(element: Element): void {
  if (!(element instanceof HTMLElement)) {
    element.removeAttribute("style");
    return;
  }
  const style = element.style;
  const kept: string[] = [];
  for (const [property, patterns] of Object.entries(ALLOWED_STYLE_VALUES)) {
    const value = style.getPropertyValue(property).trim();
    if (value && patterns.some((pattern) => pattern.test(value))) {
      kept.push(`${property}: ${value}`);
    }
  }
  if (kept.length > 0) {
    element.setAttribute("style", kept.join("; "));
  } else {
    element.removeAttribute("style");
  }
}

function restrictClass(element: Element): void {
  if (element.tagName !== "A") {
    element.removeAttribute("class");
    return;
  }
  const kept = Array.from(element.classList).filter((name) =>
    ALLOWED_LINK_CLASSES.has(name),
  );
  if (kept.length > 0) {
    element.setAttribute("class", kept.join(" "));
  } else {
    element.removeAttribute("class");
  }
}

/**
 * DOMPurify 로 정화한 HTML 의 `style`·`class` 를 허용 목록으로 좁혀 돌려준다.
 * DOM 이 없는 환경(SSR)에서는 그대로 돌려준다.
 */
export function restrictRichTextStyles(sanitizedHtml: string): string {
  if (typeof document === "undefined" || !sanitizedHtml) return sanitizedHtml;

  const template = document.createElement("template");
  template.innerHTML = sanitizedHtml;
  template.content.querySelectorAll("[style]").forEach(restrictStyle);
  template.content.querySelectorAll("[class]").forEach(restrictClass);
  return template.innerHTML;
}
